import { Combobox } from "@headlessui/react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ChevronDown from "../Icons/ChevronDown";
import Close from "../Icons/Close";
import NewTooltip from "../Tooltip/NewTooltip";
import { useStaffMode } from "../../../hooks/StaffModeProvider";

export interface AsyncDropDownOption {
  id: string;
  name: string;
}

interface AsyncPaginatedDropDownProps {
  title: string;
  name?: string;
  items: AsyncDropDownOption[];
  selectedValue: AsyncDropDownOption | null;
  selectedLabel?: string;
  onChange: (value: AsyncDropDownOption | null) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  onOpenChange?: (open: boolean) => void;
  error?: boolean;
  errormsj?: string;
  tooltip?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  titleClass?: string;
  ddClass?: string;
  maxHeight?: string;
  showViewMore?: boolean;
  onViewMore?: () => void;
  viewMoreLabel?: string;
}

const VIRTUALIZE_THRESHOLD = 50;
const VIRTUAL_ROW_HEIGHT_PX = 40;
const VIRTUAL_OVERSCAN = 5;
const LIST_MAX_HEIGHT_PX = 192;
const NEAR_BOTTOM_THRESHOLD_PX = 120;
const SCROLL_NUDGE_ABOVE_PREVIOUS_BOTTOM_PX = 80;

const OpenStateSync = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  useEffect(() => {
    onOpenChange(open);
  }, [open, onOpenChange]);

  return null;
};

function getVirtualSlice (
  itemCount: number,
  rowHeight: number,
  viewportHeight: number,
  scrollTop: number,
) {
  if (itemCount <= 0) {
    return { totalHeight: 0, start: 0, end: 0, paddingTop: 0, paddingBottom: 0 };
  }

  const totalHeight = itemCount * rowHeight;
  const topBound = Math.max(0, scrollTop - VIRTUAL_OVERSCAN * rowHeight);
  const bottomBound =
    scrollTop + viewportHeight + VIRTUAL_OVERSCAN * rowHeight;

  let start = Math.floor(topBound / rowHeight);
  let end = Math.ceil(bottomBound / rowHeight);
  start = Math.max(0, Math.min(start, itemCount - 1));
  end = Math.max(start + 1, Math.min(end, itemCount));

  const paddingTop = start * rowHeight;
  const paddingBottom = Math.max(0, totalHeight - end * rowHeight);

  return { totalHeight, start, end, paddingTop, paddingBottom };
}

const AsyncPaginatedDropDown: React.FC<AsyncPaginatedDropDownProps> = ({
  title,
  name,
  items,
  selectedValue,
  selectedLabel,
  onChange,
  searchQuery,
  onSearchQueryChange,
  onLoadMore,
  hasMore,
  isLoading = false,
  isLoadingMore = false,
  onOpenChange,
  error = false,
  errormsj,
  tooltip,
  disabled,
  required,
  placeholder,
  titleClass,
  ddClass,
  maxHeight,
  showViewMore = false,
  onViewMore,
  viewMoreLabel = "View more",
}) => {
  const { isStaffMode } = useStaffMode();
  const color = isStaffMode ? "secondary" : "primary";
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const wasSentinelIntersectingRef = useRef(false);
  const loadScrollSnapshotRef = useRef<{
    scrollHeight: number;
    scrollTop: number;
    clientHeight: number;
  } | null>(null);
  const prevIsLoadingMoreRef = useRef(false);
  const prevItemsLengthRef = useRef(0);
  const [inputWidth, setInputWidth] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const displayLabel = selectedLabel || selectedValue?.name || "";
  const shouldVirtualize = items.length > VIRTUALIZE_THRESHOLD;

  const triggerLoadMore = useCallback(() => {
    const root = listRef.current;
    if (root) {
      loadScrollSnapshotRef.current = {
        scrollHeight: root.scrollHeight,
        scrollTop: root.scrollTop,
        clientHeight: root.clientHeight,
      };
    }
    onLoadMore();
  }, [onLoadMore]);

  useEffect(() => {
    const wasLoadingMore = prevIsLoadingMoreRef.current;
    prevIsLoadingMoreRef.current = isLoadingMore;

    if (
      wasLoadingMore &&
      !isLoadingMore &&
      items.length > prevItemsLengthRef.current
    ) {
      const root = listRef.current;
      const snapshot = loadScrollSnapshotRef.current;

      if (root && snapshot) {
        requestAnimationFrame(() => {
          const distanceFromBottomBefore =
            snapshot.scrollHeight -
            snapshot.scrollTop -
            snapshot.clientHeight;
          const wasNearBottom =
            distanceFromBottomBefore <= NEAR_BOTTOM_THRESHOLD_PX;

          if (wasNearBottom) {
            const previousMaxScroll = Math.max(
              0,
              snapshot.scrollHeight - snapshot.clientHeight,
            );
            const targetScrollTop = Math.max(
              0,
              previousMaxScroll - SCROLL_NUDGE_ABOVE_PREVIOUS_BOTTOM_PX,
            );
            root.scrollTop = targetScrollTop;
            setScrollTop(targetScrollTop);
            wasSentinelIntersectingRef.current = false;
          }

          loadScrollSnapshotRef.current = null;
        });
      }
    }

    prevItemsLengthRef.current = items.length;
  }, [isLoadingMore, items.length]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onOpenChange?.(open);
      if (!open) {
        setScrollTop(0);
        wasSentinelIntersectingRef.current = false;
        loadScrollSnapshotRef.current = null;
        prevItemsLengthRef.current = 0;
        prevIsLoadingMoreRef.current = false;
      }
    },
    [onOpenChange],
  );

  const handleClearSelection = () => {
    if (disabled) {
      return;
    }
    onSearchQueryChange("");
    onChange(null);
  };

  const resolveDisplayValue = (): string => {
    if (isOpen) {
      return searchQuery;
    }
    return displayLabel;
  };

  useEffect(() => {
    const updateInputWidth = () => {
      if (inputRef.current) {
        setInputWidth(inputRef.current.offsetWidth);
      }
    };

    updateInputWidth();
    window.addEventListener("resize", updateInputWidth);
    return () => window.removeEventListener("resize", updateInputWidth);
  }, []);

  useEffect(() => {
    if (inputWidth < 103) {
      inputRef.current?.setAttribute("placeholder", "");
    } else {
      inputRef.current?.setAttribute(
        "placeholder",
        placeholder ? placeholder : "Select One",
      );
    }
  }, [inputWidth, placeholder]);

  useEffect(() => {
    if (displayLabel && inputRef.current) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.scrollLeft = 0;
          inputRef.current.setSelectionRange(0, 0);
        }
      }, 0);
    }
  }, [displayLabel]);

  useEffect(() => {
    if (!isOpen || !hasMore || isLoading || isLoadingMore) {
      return;
    }

    const root = listRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!root || !sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isIntersecting = entry.isIntersecting;
          const wasIntersecting = wasSentinelIntersectingRef.current;

          if (isIntersecting && !wasIntersecting) {
            wasSentinelIntersectingRef.current = true;
            triggerLoadMore();
            return;
          }

          if (!isIntersecting) {
            wasSentinelIntersectingRef.current = false;
          }
        });
      },
      { root, rootMargin: "80px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, isLoadingMore, isOpen, items.length, triggerLoadMore]);

  const virtualSlice = useMemo(
    () =>
      getVirtualSlice(
        items.length,
        VIRTUAL_ROW_HEIGHT_PX,
        LIST_MAX_HEIGHT_PX,
        scrollTop,
      ),
    [items.length, scrollTop],
  );

  const visibleItems = shouldVirtualize
    ? items.slice(virtualSlice.start, virtualSlice.end)
    : items;

  const renderOption = (object: AsyncDropDownOption, keyPrefix: string) => (
    <Combobox.Option
      key={`${keyPrefix}-${object.id}`}
      value={object}
      className={({ active }) =>
        `px-2 md:px-4 py-2 text-base md:text-lg leading-[19px] text-left cursor-pointer ${active || displayLabel === object.name
          ? `bg-${color} !text-white`
          : "bg-white hover:bg-gray-50"
        }`
      }
    >
      {object.name}
    </Combobox.Option>
  );

  const renderOptionsList = () => {
    if (isLoading && items.length === 0) {
      return (
        <div className="px-2 md:px-4 py-2 text-base md:text-lg text-gray-500 text-center">
          Loading...
        </div>
      );
    }

    if (!isLoading && items.length === 0) {
      return (
        <div className="px-2 md:px-4 py-2 text-base md:text-lg text-gray-500 text-center">
          No results found
        </div>
      );
    }

    if (shouldVirtualize) {
      return (
        <div style={{ height: virtualSlice.totalHeight, position: "relative" }}>
          <div style={{ height: virtualSlice.paddingTop }} aria-hidden />
          {visibleItems.map((object, index) => (
            <div
              key={object.id}
              className="absolute left-0 right-0"
              style={{
                top: (virtualSlice.start + index) * VIRTUAL_ROW_HEIGHT_PX,
                height: VIRTUAL_ROW_HEIGHT_PX,
              }}
            >
              {renderOption(object, "virtual")}
            </div>
          ))}
          <div style={{ height: virtualSlice.paddingBottom }} aria-hidden />
        </div>
      );
    }

    return items.map((object) => renderOption(object, "flat"));
  };

  return (
    <div
      className={`${ddClass} flex flex-col gap-2 md:gap-[.625rem] h-max w-full ${error ? "bottom-0 relative" : ""
        }`}
    >
      {title && (
        <div className="flex gap-2 w-full">
          <label
            className={`${titleClass} text-base md:text-lg font-medium text-gray-font`}
          >
            {title} {required && <span className="text-primary"> *</span>}
          </label>
          {tooltip && <NewTooltip text={tooltip} />}
        </div>
      )}

      <Combobox
        value={selectedValue}
        onChange={(value) => {
          onSearchQueryChange("");
          onChange(value);
        }}
        disabled={disabled}
        by="id"
      >
        {({ open }) => (
          <div className="relative">
            <OpenStateSync open={open} onOpenChange={handleOpenChange} />
            <div className={`relative ${error && "errorInput"}`}>
              <Combobox.Button className="w-full">
                <div className="flex flex-row relative">
                  <Combobox.Input
                    ref={inputRef}
                    displayValue={resolveDisplayValue}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    className={`${error ? "text-primary" : "text-input-gray"
                      } ${ddClass}`}
                    style={
                      displayLabel
                        ? {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textAlign: "left",
                          direction: "ltr",
                        }
                        : undefined
                    }
                    onChange={(event) => onSearchQueryChange(event.currentTarget.value)}
                    name={name || title}
                  />
                  {displayLabel && (
                    <div
                      className={`absolute top-1/2 -translate-y-1/2 right-10 h-4 w-4 md:h-5 md:w-5 cursor-pointer z-1 ${disabled
                          ? "fill-gray-300 pointer-events-none"
                          : "fill-gray-font"
                        }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearSelection();
                      }}
                    >
                      <Close />
                    </div>
                  )}
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <div
                      className={`${disabled ? "text-gray-200" : "text-gray-font"
                        } w-5 h-5 md:w-6 md:h-6 ${open && "rotate-180 duration-75"
                        } ${disabled ? "fill-gray-300" : "fill-gray-font"}`}
                    >
                      <ChevronDown />
                    </div>
                  </div>
                </div>
              </Combobox.Button>
            </div>

            <Combobox.Options
              className={`absolute top-14 w-full ${maxHeight || "max-h-48"
                } z-30 ${!open && "hidden"} bg-white rounded-sm shadow-2xl overflow-hidden`}
            >
              <div
                ref={listRef}
                className={`${maxHeight || "max-h-48"} overflow-y-auto`}
                onScroll={(event) => {
                  setScrollTop(event.currentTarget.scrollTop);
                }}
              >
                {renderOptionsList()}
                {showViewMore && onViewMore && (
                  <button
                    type="button"
                    className="w-full px-2 md:px-4 py-2.5 text-sm font-medium text-left text-Default border-t border-gray-100 hover:bg-gray-50"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onViewMore();
                    }}
                  >
                    {viewMoreLabel}
                  </button>
                )}
                {hasMore && <div ref={loadMoreSentinelRef} className="h-1" aria-hidden />}
                {isLoadingMore && (
                  <div className="px-2 md:px-4 py-2 text-sm text-gray-500 text-center">
                    Loading more...
                  </div>
                )}
              </div>
            </Combobox.Options>
          </div>
        )}
      </Combobox>
      {error && errormsj && (
        <span className="flex flex-wrap items-center text-base font-medium text-primary">
          {errormsj}
        </span>
      )}
    </div>
  );
};

export default AsyncPaginatedDropDown;
